import {
  LOAD_PAYER,
  LOAD_PAYER_USER,
  PAL_REFRESH,
  getPalViewAsRefreshRetryDdaIds,
  sanitizePalViewAsRefreshResponse,
} from 'model/viewAs';

import {
  call,
  delay,
  put,
  race,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  getPalViewAsRefreshStatus,
  registerPayer,
  startPalViewAsRefresh,
} from 'sdk/payer';
import { callAction, ORM } from '../util';

export const PAL_REFRESH_FOREGROUND_BUDGET_MS = 5000;
export const PAL_REFRESH_POLL_INTERVAL_MS = 250;
export const PAL_REFRESH_MAX_STATUS_REQUESTS = 3;

const terminalStatuses = new Set([
  'complete',
  'partial',
  'failed',
  'awaiting_proof',
]);
const knownStatuses = new Set([
  'queued',
  'starting',
  'running',
  'partial',
  'complete',
  'failed',
  'awaiting_proof',
]);

const getStatusCode = (error) =>
  error?.status ?? error?.statusCode ?? error?.response?.status;

const getErrorBody = (error) => error?.data ?? error?.response?.data ?? {};

const getResponseBody = (response) =>
  response?.data &&
  typeof response.data === 'object' &&
  (response.data.status || response.data.operationId)
    ? response.data
    : response;

const getPublicResponse = (response) =>
  sanitizePalViewAsRefreshResponse(getResponseBody(response));

const createAutomaticRetryRequestId = (requestId) =>
  `${requestId || 'pal-refresh'}-auto-retry`;

const isTerminal = (response) =>
  terminalStatuses.has(getResponseBody(response)?.status);

const isKnownStatus = (response) =>
  knownStatuses.has(getResponseBody(response)?.status);

const takeMatchingCancel = (requestId) =>
  take(
    (action) =>
      action.type === PAL_REFRESH.CANCEL &&
      (!action.data?.requestId || action.data.requestId === requestId)
  );

export const isWithinPalRefreshBudget = (
  startedAt,
  now = Date.now(),
  budgetMs = PAL_REFRESH_FOREGROUND_BUDGET_MS
) => now - startedAt < budgetMs;

const putRefreshStatus = (type, data, response, metadata = {}) =>
  put({
    type,
    data: {
      ...data,
      ...metadata,
      response: getPublicResponse(response),
    },
  });

const waitForPoll = function* waitForPoll(requestId) {
  const result = yield race({
    tick: delay(PAL_REFRESH_POLL_INTERVAL_MS),
    cancel: takeMatchingCancel(requestId),
  });
  return !result.cancel;
};

const callWithCancel = function* callWithCancel(
  fn,
  args,
  requestId,
  timeoutMs
) {
  const effects = {
    response: call(fn, args),
    cancel: takeMatchingCancel(requestId),
  };
  if (Number.isFinite(timeoutMs)) {
    effects.timeout = delay(timeoutMs);
  }
  const result = yield race(effects);

  if (Object.prototype.hasOwnProperty.call(result, 'response')) {
    return { response: result.response };
  }
  if (result.timeout) {
    return { timedOut: true };
  }

  return { cancelled: Boolean(result.cancel) };
};

export function* refreshPalViewAs({ data = {} }) {
  const { ddaIds, fspId, payerId, requestId, userId } = data;
  const context = { fspId, payerId, requestId };

  if (data.enabled !== true || !fspId || !payerId || !userId) {
    yield put({
      type: PAL_REFRESH.FAILURE,
      data: { ...context, status: 'unavailable' },
    });
    return;
  }

  let startResult;
  try {
    startResult = yield* callWithCancel(
      startPalViewAsRefresh,
      {
        ...(Array.isArray(ddaIds) && ddaIds.length > 0 ? { ddaIds } : {}),
        fspId,
        payerId,
        userId,
      },
      requestId,
      PAL_REFRESH_FOREGROUND_BUDGET_MS
    );
  } catch (error) {
    const statusCode = getStatusCode(error);
    const body = getErrorBody(error);
    if (statusCode === 404 && body.status === 'disabled') {
      yield put({
        type: PAL_REFRESH.FAILURE,
        data: { ...context, status: 'disabled' },
      });
      return;
    }

    yield put({
      type: PAL_REFRESH.FAILURE,
      data: { ...context, status: 'unavailable' },
    });
    return;
  }
  if (startResult.cancelled) {
    return;
  }
  if (startResult.timedOut) {
    yield put({
      type: PAL_REFRESH.FAILURE,
      data: { ...context, status: 'timeout' },
    });
    return;
  }
  const startResponse = startResult.response;

  const startBody = getResponseBody(startResponse);
  if (startBody?.status === 'disabled') {
    yield put({
      type: PAL_REFRESH.FAILURE,
      data: { ...context, status: 'disabled' },
    });
    return;
  }
  if (startBody?.status === 'not_eligible') {
    yield put({
      type: PAL_REFRESH.FAILURE,
      data: { ...context, status: 'not_eligible' },
    });
    return;
  }
  if (!isKnownStatus(startBody)) {
    yield put({
      type: PAL_REFRESH.FAILURE,
      data: { ...context, status: 'unknown' },
    });
    return;
  }

  if (!isTerminal(startBody)) {
    yield putRefreshStatus(PAL_REFRESH.SUCCESS, context, startBody);
  }

  let operationId = startBody?.operationId;
  if (typeof operationId !== 'string' || operationId.length === 0) {
    yield put({
      type: PAL_REFRESH.FAILURE,
      data: { ...context, status: 'unknown' },
    });
    return;
  }

  const startedAt = Date.now();
  let statusRequestCount = 0;
  let lastBody = startBody;
  while (
    statusRequestCount < PAL_REFRESH_MAX_STATUS_REQUESTS &&
    isWithinPalRefreshBudget(startedAt)
  ) {
    const shouldPoll = yield* waitForPoll(requestId);
    if (!shouldPoll) {
      return;
    }

    if (!isWithinPalRefreshBudget(startedAt)) {
      break;
    }

    statusRequestCount += 1;
    let statusResult;
    try {
      statusResult = yield* callWithCancel(
        getPalViewAsRefreshStatus,
        { operationId },
        requestId,
        PAL_REFRESH_FOREGROUND_BUDGET_MS - (Date.now() - startedAt)
      );
    } catch (error) {
      const statusCode = getStatusCode(error);
      const body = getErrorBody(error);
      if (statusCode === 404 && body.status === 'expired') {
        yield put({
          type: PAL_REFRESH.FAILURE,
          data: { ...context, status: 'expired' },
        });
      } else {
        yield put({
          type: PAL_REFRESH.FAILURE,
          data: { ...context, status: 'unavailable' },
        });
      }
      return;
    }
    if (statusResult.cancelled) {
      return;
    }
    if (statusResult.timedOut) {
      break;
    }
    const statusResponse = statusResult.response;

    const statusBody = getResponseBody(statusResponse);
    if (!isKnownStatus(statusBody)) {
      yield put({
        type: PAL_REFRESH.FAILURE,
        data: { ...context, status: 'unknown' },
      });
      return;
    }
    lastBody = statusBody;

    // The BFF rolls a signed token carrying merged DDA state; keep it saga-local, never in Redux.
    if (
      typeof statusBody.operationId === 'string' &&
      statusBody.operationId.length > 0
    ) {
      operationId = statusBody.operationId;
    }

    if (isTerminal(statusBody)) {
      const retryDdaIds =
        statusBody.status === 'failed' || data.autoRetryAttempted
          ? []
          : getPalViewAsRefreshRetryDdaIds(statusBody);
      if (retryDdaIds.length === 0) {
        break;
      }
      yield putRefreshStatus(PAL_REFRESH.STATUS, context, statusBody);
      yield put({
        type: PAL_REFRESH.ACTION,
        data: {
          ...data,
          autoRetryAttempted: true,
          ddaIds: retryDdaIds,
          requestId: createAutomaticRetryRequestId(requestId),
        },
      });
      return;
    }
    if (statusRequestCount === PAL_REFRESH_MAX_STATUS_REQUESTS) {
      break;
    }
    yield putRefreshStatus(PAL_REFRESH.STATUS, context, statusBody);
  }

  // The UI opens the View As tab on pollingComplete, so every settle path ends here regardless of DDA readiness.
  yield putRefreshStatus(PAL_REFRESH.STATUS, context, lastBody, {
    pollingComplete: true,
  });
}

export default function* saga() {
  yield takeEvery(
    LOAD_PAYER.ACTION,
    callAction({
      api: registerPayer,
      dataName: ORM,
      type: LOAD_PAYER,
    })
  );

  yield takeEvery(
    LOAD_PAYER_USER.ACTION,
    callAction({
      api: registerPayer,
      dataName: ORM,
      type: LOAD_PAYER_USER,
    })
  );

  yield takeLatest(PAL_REFRESH.ACTION, refreshPalViewAs);
}
